/**
 * Typed calls for the user-management P1 screens: moderation, bans, the
 * audit log, invites, search, nicknames, pokes, ignore, message requests,
 * the recovery ladder, ownership transfer and recently deleted items.
 * Paths come from `Routes` and `P1Routes` (packages/protocol), the
 * server's contract.
 */
import {
  P1Routes,
  Routes,
  type AcceptInviteResponse,
  type AuditLogQuery,
  type AuthSessionResponse,
  type Channel,
  type ChannelVoiceMute,
  type AuditLogResponse,
  type Ban,
  type CreateBanRequest,
  type CreateInviteRequest,
  type DeletedItem,
  type GuildMember,
  type Invite,
  type MessageSearchQuery,
  type MessageSearchResponse,
  type ModifyChannelPositionsRequest,
  type ModifyCurrentMemberRequest,
  type ModifyMemberRequest,
  type Message,
  type OwnerTransfer,
  type PokeRequest,
  type PutChannelVoiceMuteRequest,
  type RecoveryActionRequest,
  type RecoveryCompleteRequest,
  type RecoveryRequestInfo,
  type RecoveryStartRequest,
  type RecoveryStartResponse,
  type RestoreDeletedResponse,
  type SetMessageRequestBody,
  type Snowflake,
  type StartOwnerTransferRequest,
  type User,
} from '@neosocial/protocol';
import type { ApiClient } from './client';

type Query = Record<string, string | number | boolean | undefined | null>;

export function createUmApi(client: ApiClient) {
  return {
    // members and moderation (Discord's public shapes)
    modifyMember: (g: Snowflake, u: Snowflake, b: ModifyMemberRequest, reason?: string) =>
      client.patch<GuildMember>(Routes.guildMember(g, u), b, { reason }),
    modifyMe: (g: Snowflake, b: ModifyCurrentMemberRequest) =>
      client.patch<GuildMember>(Routes.guildMember(g, '@me'), b),
    kick: (g: Snowflake, u: Snowflake, reason?: string) =>
      client.delete<void>(Routes.guildMember(g, u), { reason }),
    ban: (g: Snowflake, u: Snowflake, b: CreateBanRequest, reason?: string) =>
      client.put<void>(Routes.guildBan(g, u), b, { reason }),
    unban: (g: Snowflake, u: Snowflake, reason?: string) =>
      client.delete<void>(Routes.guildBan(g, u), { reason }),
    bans: (g: Snowflake) => client.get<Ban[]>(Routes.guildBans(g), { query: { limit: 1000 } }),
    // channel mute and deafen (2026-10-28): one voice channel only
    channelVoiceMutes: (c: Snowflake) =>
      client.get<ChannelVoiceMute[]>(Routes.channelVoiceMutes(c)),
    setChannelVoiceMute: (
      c: Snowflake,
      u: Snowflake,
      b: PutChannelVoiceMuteRequest,
      reason?: string,
    ) => client.put<ChannelVoiceMute>(Routes.channelVoiceMute(c, u), b, { reason }),
    liftChannelVoiceMute: (c: Snowflake, u: Snowflake, reason?: string) =>
      client.delete<void>(Routes.channelVoiceMute(c, u), { reason }),
    auditLog: (g: Snowflake, q: AuditLogQuery) =>
      client.get<AuditLogResponse>(Routes.guildAuditLogs(g), { query: { ...q } as Query }),
    // invites
    createInvite: (c: Snowflake, b: CreateInviteRequest) =>
      client.post<Invite>(Routes.channelInvites(c), b),
    invites: (g: Snowflake) => client.get<Invite[]>(Routes.guildInvites(g)),
    revokeInvite: (code: string) => client.delete<Invite>(Routes.invite(code)),
    // an invite link (/invite/CODE): what it is, and joining through it
    invite: (code: string) => client.get<Invite>(Routes.invite(code), { auth: false }),
    acceptInvite: (code: string) => client.post<AcceptInviteResponse>(Routes.invite(code), {}),
    // search
    search: (g: Snowflake, q: MessageSearchQuery) =>
      client.get<MessageSearchResponse>(Routes.guildMessagesSearch(g), {
        query: { ...q } as Query,
      }),
    searchChannel: (c: Snowflake, q: MessageSearchQuery) =>
      client.get<MessageSearchResponse>(Routes.channelMessagesSearch(c), {
        query: { ...q } as Query,
      }),
    // social
    poke: (u: Snowflake, b: PokeRequest) => client.post<void>(Routes.userPoke(u), b),
    ignores: () => client.get<User[]>(P1Routes.meIgnores()),
    ignore: (u: Snowflake) => client.put<void>(P1Routes.meIgnore(u)),
    unignore: (u: Snowflake) => client.delete<void>(P1Routes.meIgnore(u)),
    /** Pending requests: 1:1 DMs with `nx_request_state: 'pending'`. */
    messageRequests: () => client.get<Channel[]>(P1Routes.meMessageRequests()),
    /** The newest message of a request, for its preview (null when there is none). */
    lastMessage: async (c: Snowflake): Promise<Message | null> =>
      (await client.get<Message[]>(Routes.channelMessages(c), { query: { limit: 1 } }))[0] ?? null,
    /** Accepting returns the DM; dismissing returns nothing (204). */
    setRequest: (c: Snowflake, b: SetMessageRequestBody) =>
      client.put<Channel | null | undefined>(P1Routes.channelRequest(c), b),
    // the recovery ladder (no auth on the requesting side)
    recoveryStart: (b: RecoveryStartRequest) =>
      client.post<RecoveryStartResponse>(P1Routes.recoveryStart(), b, { auth: false }),
    /** 403 RECOVERY_NOT_READY while the hold runs or no device has approved yet. */
    recoveryComplete: (b: RecoveryCompleteRequest) =>
      client.post<AuthSessionResponse>(P1Routes.recoveryComplete(), b, { auth: false }),
    recoveryPending: () => client.get<RecoveryRequestInfo[]>(P1Routes.recoveryPending()),
    recoveryCancel: (b: RecoveryActionRequest) => client.post<void>(P1Routes.recoveryCancel(), b),
    recoveryApprove: (b: RecoveryActionRequest) => client.post<void>(P1Routes.recoveryApprove(), b),
    // ownership and restore
    startOwnerTransfer: (g: Snowflake, b: StartOwnerTransferRequest) =>
      client.post<OwnerTransfer>(P1Routes.guildOwnerTransfer(g), b),
    deleted: (g: Snowflake) => client.get<DeletedItem[]>(P1Routes.guildDeleted(g)),
    restore: (g: Snowflake, id: Snowflake) =>
      client.post<RestoreDeletedResponse>(P1Routes.guildDeletedRestore(g, id), {}),
    // drag reordering: positions, and a new category (store/reorder.ts channelDropBody)
    channelPositions: (g: Snowflake, b: ModifyChannelPositionsRequest) =>
      client.patch<void>(Routes.guildChannels(g), b),
    rolePositions: (g: Snowflake, b: Array<{ id: Snowflake; position: number }>) =>
      client.patch<unknown>(Routes.guildRoles(g), b),
    // members of a role (Server settings > Roles > Members)
    addMemberRole: (g: Snowflake, u: Snowflake, r: Snowflake) =>
      client.put<void>(Routes.guildMemberRole(g, u, r), {}),
    removeMemberRole: (g: Snowflake, u: Snowflake, r: Snowflake) =>
      client.delete<void>(Routes.guildMemberRole(g, u, r)),
  };
}

export type UmApi = ReturnType<typeof createUmApi>;
